import { CONVERSATIONAL_MM_AGENT } from '@fruitful/llm-service-shared';
import { AnalyticsUserEvent } from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';

import { StorageKeys } from '../../common/constants';
import storage from '../../common/providers/StorageFacade';
import {
    captureException,
    captureWarning,
    track,
    trackGrowthMarketingEvent,
} from '../../logger/Logger';
import { useVisitorId } from '../../user/providers/VisitorProvider';
import { onboardingChatResultsAtom } from '../state/OnboardingChatAtoms';
import { useUpsertMastraThread } from './useUpsertMastraThread';

/**
 * Shared Continue handler for CMM results CTAs (debt/savings GoalsSlide and
 * invest InvestingCtaSlide). Best-effort upserts the current atom (including
 * slider adjustments and slide-derived fields like goalCompletionDates /
 * investmentWaterfall) to the Mastra thread, then navigates into signup.
 *
 * Only call from pre-signup flows wrapped in VisitorProvider — never from the
 * homepage results overlay (mount the CTA in a child that only renders when
 * onExit is null, matching TrialSignupCard / TrialSignupContinueButton).
 */
export const useSubmitOnboardingChatResults = () => {
    const onboardingChatResults = useAtomValue(onboardingChatResultsAtom);
    const threadId = storage.getString(StorageKeys.CONVERSATIONAL_MM_THREAD_ID);
    const visitorId = useVisitorId();
    const { upsertMastraThread } = useUpsertMastraThread();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const searchParams = useLocalSearchParams();

    const submitOnboardingChatResults = useCallback(async () => {
        setIsSubmitting(true);

        if (
            onboardingChatResults != null &&
            threadId != null &&
            threadId !== ''
        ) {
            try {
                await upsertMastraThread({
                    mastraThreadId: threadId,
                    agentId: CONVERSATIONAL_MM_AGENT.id,
                    visitorId,
                    version: '1.0',
                    results: onboardingChatResults,
                });
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : String(error);
                track(
                    AnalyticsUserEvent.CMM_RESULTS_THREAD_SAVE_ERROR,
                    {
                        threadId,
                        errorMessage,
                    },
                    /* sendImmediately */ true,
                );
                captureException(new Error(`Error upserting mastra thread`), {
                    error: error as Error,
                    threadId,
                });
            }
        }

        try {
            storage.setString(
                StorageKeys.SIGNUP_FLOW_SELECTED_GUIDE,
                '{}', // Value is read by webflow site code, JSON string expected; We clear this because we don't want previous signup flow sessions to inadvertently affect new flow behavior
            );
            storage.setString(
                StorageKeys.SIGNUP_FLOW_VERSION,
                'without-guide', // A constant defined in webflow site code, required during "no-guide-selection" experiment in order to complete signup in webflow without selected guide
            );
        } catch (error) {
            captureWarning(
                `Error writing to storage for signup flow`,
                error as Error,
            );
        }

        track(
            AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_RESULTS_SUBMIT,
            {
                threadId: threadId ?? '',
                visitorId: visitorId ?? '',
            },
            /* sendImmediately */ true,
        );

        trackGrowthMarketingEvent(
            AnalyticsUserEvent.SIGNUP_FUNNEL_MEMBERSHIP_SUBMIT,
        );
        router.navigate({
            pathname: '/sign-up/new-member',
            params: searchParams,
        });
    }, [
        onboardingChatResults,
        threadId,
        upsertMastraThread,
        visitorId,
        searchParams,
    ]);

    return {
        isSubmitting,
        submitOnboardingChatResults,
    };
};
